import { Box, CircularProgress, Divider, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DialogBox, DialogBoxContent, DialogBoxTitle } from "../ui/DialogBox";
import { ReactComponent as SuccessIcon } from "../../assets/icons/status-popup/success.svg";
import { ReactComponent as ErrorIcon } from "../../assets/icons/status-popup/error.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/status-popup/info.svg";
import { PrimaryButton } from "../ui/Button";
import { useRef, useState } from "react";
import { encodeToBase64 } from "../../utils/helpers";
import authServices from "../../services/api/service/authService";
import { useDispatch, useSelector } from "react-redux";
import {
  authSelector,
  setUserProfileData,
} from "../../services/redux/slices/authSlice";
import maxFileSize from "../../../src/data/constant";
type ProfilePicUploadPopupProps = {
  open: boolean;
  handleClose: () => void;
};

const ProfilePicUploadPopup = ({
  open,
  handleClose,
}: ProfilePicUploadPopupProps) => {
  const dispatch = useDispatch();
  const { userProfile } = useSelector(authSelector);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const showStatusIcon = () => {
    if (uploadError == null && !uploadSuccess) {
      return <InfoIcon />;
    }
    if (uploadError && !uploadSuccess) {
      return <ErrorIcon />;
    }
    if (uploadSuccess && uploadError == null) {
      return <SuccessIcon />;
    }
  };

  const showStatusMessage = () => {
    if (uploadError == null && !uploadSuccess)
      return (
        <Box sx={{ paddingInline: "8px" }}>
          Image can be JPEG, JPG, PNG and cannot exceed 10MB.
        </Box>
      );
    if (uploadError && !uploadSuccess) {
      return (
        <Box sx={{ paddingInline: "8px", color: "red" }}>{uploadError}</Box>
      );
    }
    if (uploadSuccess && uploadError == null) {
      return (
        <Box sx={{ paddingInline: "8px" }}>
          Your picture uploaded successfully
        </Box>
      );
    }
  };

  const showStatusButton = () => {
    if (!uploadSuccess)
      return (
        <PrimaryButton
          onClick={() => {
            !isLoading && fileInputRef?.current?.click();
          }}
          sx={{ minWidth: "244px" }}
        >
          {!isLoading ? (
            "Choose Image"
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress
                sx={{
                  color: "black",
                  height: "20px !important",
                  width: "20px !important",
                }}
              />
            </Box>
          )}
        </PrimaryButton>
      );
    else {
      return (
        <PrimaryButton onClick={() => handleClose()} sx={{ minWidth: "244px" }}>
          Close
        </PrimaryButton>
      );
    }
  };

  const uploadUserProfilePicture = (base64: any) => {
    const plainBase64Data = base64.replace(/^data:(.*?);base64,/, "");
    const payload = {
      profileImage: plainBase64Data,
    };
    setIsLoading(true);
    authServices
      ?.uploadImage(payload)
      .then((res) => {
        if (res?.status === 200) {
          const userProfileCopy = JSON.parse(JSON.stringify(userProfile));
          userProfileCopy.profileImage = res?.data?.data;
          dispatch(setUserProfileData(userProfileCopy));
          setUploadSuccess(true);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setUploadError(
          error.response?.data?.errors[0]?.constraints?.isBase64Image || "An error occurred while uploading"
        );
      });
  };

  const onImageChange = (e: any) => {
    setUploadError(null);
    if (
      e.target?.files?.[0] &&
      !["image/jpeg", "image/jpg", "image/png"].includes(e.target.files[0].type)
    ) {
      setUploadError("Only JPEG, JPG, and PNG files are allowed.");
      setIsLoading(false);
    }
    else if (e.target?.files?.[0]?.size > maxFileSize) {
      setUploadError("Your image size exceeds maximum limit of 5 MB");
      setIsLoading(false);
    }
    else if (e.target?.files?.[0]) {
      encodeToBase64(e?.target?.files[0])
        .then((res) => {
          uploadUserProfilePicture(res);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  return (
    <DialogBox open={open}>
      <DialogBoxTitle id="customized-dialog-title">
        Profile Picture
      </DialogBoxTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogBoxContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "24px",
          }}
        >
          {showStatusIcon()}
          <Box sx={{ display: "none" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png, .jpg, .jpeg"
              onChange={onImageChange}
            />
          </Box>
          {showStatusMessage()}
          {showStatusButton()}
        </Box>
      </DialogBoxContent>
    </DialogBox>
  );
};

export default ProfilePicUploadPopup;
